import React, { useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import moment from "moment";
import { Modal } from "antd";
import { useDispatch } from "react-redux";
import { deleteSettlement } from "../../redux/settlementSlice";
import { formatPrice, getCurrencySymbol } from "../../utils/currency";
import "./ag-grid-custom.css";

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

const SettlementsDataTableAG = ({ settlements, loading }) => {
  const [modal, contextHolder] = Modal.useModal();
  const dispatch = useDispatch();

  const handleDelete = useCallback((sid) => {
    modal.confirm({
      title: "Are you sure you want to delete this settlement?",
      onOk: async () => {
        await dispatch(deleteSettlement({ settlementId: sid }));
      },
    });
  }, [modal, dispatch]);

  const columnDefs = useMemo(
    () => [
      {
        field: "created",
        headerName: "Created",
        width: 120,
        pinned: "left",
        filter: "agDateColumnFilter",
        sortable: true,
        comparator: (date1, date2) => {
          if (!date1 && !date2) return 0;
          if (!date1) return -1;
          if (!date2) return 1;
          return new Date(date1) - new Date(date2);
        },
        valueGetter: (params) => {
          return params.data?.createdAt ? new Date(params.data.createdAt) : null;
        },
        valueFormatter: (params) => {
          return params.value ? moment(params.value).format("l") : "";
        },
      },
      {
        field: "publisher",
        headerName: "Publisher",
        width: 200,
        pinned: "left",
        filter: "agTextColumnFilter",
        sortable: true,
        valueGetter: (params) => params.data?.publisher?.name || "",
      },
      {
        field: "website",
        headerName: "Website",
        width: 250,
        filter: "agTextColumnFilter",
        sortable: true,
        valueGetter: (params) => params.data?.publisher?.website || "",
      },
      {
        field: "settled",
        headerName: "Settled",
        width: 150,
        filter: "agNumberColumnFilter",
        sortable: true,
        valueGetter: (params) => params.data?.amount || 0,
        valueFormatter: (params) => {
          const cur = params.data?.publisher?.stripe?.currency || 'usd';
          return getCurrencySymbol(cur) + formatPrice(params.value, cur);
        },
      },
      {
        field: "lastSettlementDate",
        headerName: "Last settlement date",
        width: 180,
        filter: "agDateColumnFilter",
        sortable: true,
        valueGetter: (params) => {
          return params.data?.lastSettlement ? new Date(params.data.lastSettlement) : null;
        },
        valueFormatter: (params) => {
          return params.value ? moment(params.value).format("l") : "";
        },
      },
      {
        field: "daysSinceLastSettlement",
        headerName: "Days since last settlement",
        width: 200,
        filter: "agNumberColumnFilter",
        sortable: true,
        valueGetter: (params) => {
          if (!params.data?.lastSettlement) return 0;
          return moment()
            .startOf("day")
            .diff(moment(params.data.lastSettlement).startOf("day"), "days");
        },
      },
      {
        field: "action",
        headerName: "Actions",
        width: 120,
        pinned: "right",
        filter: false,
        sortable: false,
        cellRenderer: (params) => {
          const id = params.data?.id;
          if (!id) return null;
          return (
            <span
              className="actionBtn"
              onClick={() => handleDelete(id)}
              style={{ cursor: "pointer", color: "#1677ff" }}
            >
              [delete]
            </span>
          );
        },
      },
    ],
    [handleDelete]
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      filter: true,
    }),
    []
  );

  const rowData = useMemo(() => settlements || [], [settlements]);

  return (
    <>
      {contextHolder}
      <div
        className="ag-theme-alpine"
        style={{ height: "700px", width: "100%" }}
      >
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          loading={loading}
          pagination={true}
          paginationPageSize={50}
          suppressMenuHide={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
        />
      </div>
    </>
  );
};

export default SettlementsDataTableAG;
